import { GameScene } from '../../GameScene';
import {
  ICollision,
  CollideHandler,
} from '../../actions/collisions/ICollision';
import { InvalidEffect } from '../../actions/effects/InvalidEffect';
import { CollideId, CollideEvent, isOneHitCollider } from '../../defs/collide';
import {
  despawnBlockEntity,
  setBlockEntityViewLayer,
} from '../../entities/BlockEntity';
import { LaserBoxBlockEntity } from './LaserBoxBlockEntity';
import { LaserBoxBlockShootEffect } from './LaserBoxBlockShootEffect';

export class LaserBoxBlockCollision implements ICollision {
  private readonly scene: GameScene;
  private readonly entity: LaserBoxBlockEntity;
  private readonly resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this.resolveTap,
    block: this.resolveBlock,
    attack: this.resolveAttack,
  };

  constructor(scene: GameScene, entity: LaserBoxBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this.resolvers[event.id]?.call(this, event) ?? false;
  }

  private async resolveTap() {
    await new InvalidEffect(this.entity).execute();
    return false;
  }

  private async resolveBlock(event: CollideEvent) {
    await this.hit(event);
    return true;
  }

  private async resolveAttack(event: CollideEvent) {
    const laserBox = this.entity.c.blockLaserBox;

    // Ignore any further attacks once the destruction flow has started.
    // Return true because the attack was handled as a no-op on an already-triggered box.
    if (laserBox.count === 0) {
      return true;
    }

    if (isOneHitCollider(event.collider?.type)) {
      // This will make the laser box destroyable with one hit
      laserBox.count = 1;
    }
    await this.hit(event);
    return true;
  }

  private async hit(event: CollideEvent) {
    const laserBox = this.entity.c.blockLaserBox;

    // Skip if count is already zero
    if (laserBox.count === 0) return;
    laserBox.count -= 1;

    if (laserBox.count === 0) {
      // Briefly bring the block to the front while animation plays
      setBlockEntityViewLayer(this.scene, this.entity, 'middle');

      // Shoot the laser
      const effect = new LaserBoxBlockShootEffect(this.scene, { laserBox });
      await effect.execute();

      // Despawn the laser box block
      despawnBlockEntity(this.scene, this.entity);
    }
  }
}
